import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, KeyRound, BarChart2, Radio } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { Card, CardHeader, CardTitle, CardBody, CardFooter, SegmentedControl, Button } from '@vnetwork/ui/primitives';
import { Chart, PieChart } from '@vnetwork/ui/charts';
import { BarList, EmptyState } from '@vnetwork/ui/data';
import { useAesAnalytics, type DrmStream, type DrmUsageRow } from '@/api/aes';
import { fmtInt } from './shared';
import styles from './Drm.module.css';

/**
 * Usage-analytics block in the project's analytics-template rhythm: a time-range
 * control over a row of ChartCards (keys-issued time series, top streams by keys,
 * stream-type split). All series are REAL data from the datalake analytics
 * endpoint + the owned per-stream usage rollup — empty states when there's none.
 */
const RANGES = [
  { value: '24h', label: '24H', ms: 24 * 3600_000, granularity: '1h' as const },
  { value: '7d', label: '7D', ms: 7 * 86_400_000, granularity: 'day' as const },
  { value: '30d', label: '30D', ms: 30 * 86_400_000, granularity: 'day' as const },
];

export function DrmAnalyticsSection({
  serviceId,
  base,
  streams,
  usageRows,
}: {
  serviceId?: string;
  base: string;
  streams: DrmStream[];
  usageRows: DrmUsageRow[];
}) {
  const navigate = useNavigate();
  const [rangeKey, setRangeKey] = useState('24h');
  const range = RANGES.find((r) => r.value === rangeKey) ?? RANGES[0];

  const { from, to } = useMemo(() => {
    const now = new Date();
    return { from: new Date(now.getTime() - range.ms).toISOString(), to: now.toISOString() };
  }, [range.ms]);

  const keysQ = useAesAnalytics(serviceId, { metric: 'keys_issued', granularity: range.granularity, from, to });

  const keySeries = useMemo(
    () =>
      (keysQ.data?.series ?? []).map((p) => ({
        t: range.granularity === '1h'
          ? new Date(p.timestamp).toLocaleTimeString([], { hour: '2-digit' })
          : new Date(p.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' }),
        keys: p.value,
      })),
    [keysQ.data, range.granularity],
  );

  const topStreams = useMemo(
    () =>
      [...usageRows]
        .sort((a, b) => (b.counters.keys_issued ?? 0) - (a.counters.keys_issued ?? 0))
        .slice(0, 6)
        .map((r) => ({ label: r.stream_id, value: r.counters.keys_issued ?? 0, display: fmtInt(r.counters.keys_issued ?? 0) })),
    [usageRows],
  );

  const kindSplit = useMemo(() => {
    const live = streams.filter((s) => s.kind === 'live').length;
    const vod = streams.filter((s) => s.kind === 'vod').length;
    return [
      { name: 'Live', value: live, color: 'var(--vn-danger-solid)' },
      { name: 'VOD', value: vod, color: 'var(--vn-primary-solid)' },
    ].filter((d) => d.value > 0);
  }, [streams]);

  const usageLink = (
    <button
      type="button"
      className={styles.cardFooterLink}
      onClick={() => navigate(`/services/aes/${serviceId}/usage?scope=self-hosted`)}
    >
      View usage ▸
    </button>
  );

  return (
    <Section
      title="Usage analytics"
      style={{ marginBottom: 0 }}
      actions={
        <SegmentedControl size="sm" items={RANGES.map((r) => ({ value: r.value, label: r.label }))} value={rangeKey} onValueChange={setRangeKey} />
      }
    >
      <div className={styles.chartGrid}>
        <Card flat>
          <CardHeader><CardTitle className={styles.cardTitle}>Keys issued</CardTitle></CardHeader>
          <CardBody>
            {keySeries.length ? (
              <Chart type="area" data={keySeries} xKey="t" series={[{ key: 'keys', label: 'Keys issued' }]} height={200} />
            ) : (
              <EmptyState icon={<KeyRound size={20} />} title="No keys issued" description="No key requests in the selected window." />
            )}
          </CardBody>
          <CardFooter>{usageLink}</CardFooter>
        </Card>

        <Card flat>
          <CardHeader><CardTitle className={styles.cardTitle}>Top streams by keys</CardTitle></CardHeader>
          <CardBody>
            {topStreams.length ? (
              <BarList items={topStreams} />
            ) : (
              <EmptyState icon={<BarChart2 size={20} />} title="No usage yet" description="Per-stream key issuance shows here." />
            )}
          </CardBody>
          <CardFooter>{usageLink}</CardFooter>
        </Card>

        <Card flat>
          <CardHeader><CardTitle className={styles.cardTitle}>Streams by type</CardTitle></CardHeader>
          <CardBody>
            {kindSplit.length ? (
              <PieChart data={kindSplit} donut height={200} />
            ) : (
              <EmptyState icon={<Radio size={20} />} title="No streams yet" description="Provision a source to see the split." />
            )}
          </CardBody>
          <CardFooter>
            <Button size="sm" variant="ghost" endIcon={<ArrowRight size={14} />} onClick={() => navigate(`${base}/streams`)}>
              All streams
            </Button>
          </CardFooter>
        </Card>
      </div>
    </Section>
  );
}

export default DrmAnalyticsSection;
